import { Col, Container, Card, Button, Table, Spinner, Modal, ListGroup, Form } from "react-bootstrap";
import Layout from "../../components/Layout";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import formatCurrency from "../../utils/currencyFormmter";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { assignOrderToDepartment, getAllDepartments, getOrderById, getStoreProducts, orderUpdate } from "../../redux/slices/inventory.slice";
import InventoryConnectionButton from "../../components/InventoryConnector";
import dateFormatter from "../../utils/dateFormatter";

const OrderDetails = () => {

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const isLoaded = useRef(false)

    const initialState = {
        loading: false,
        showForm: false,
        payload: {},
        filterPayload: {
            filter: '',
            perPage: 20,
            page: 1,
        },
        errors: {},
        orders: [],
        showOrder: false,
        order: {},
        departments: [],
        orderAssignments: {},
        products: [],
        filteredItems: [],
        selectedItems: [],
        selectedItem: null,
        sendingRequest: false,
        searching: false,
    }

    const [state, setState] = useState(initialState)
    const [searchParams] = useSearchParams()

    const { invLogin } = useSelector((state) => state.inventory)
    const { user } = useSelector((state) => state.auth)
    const { id: orderId } = useParams()
    const department = searchParams.get("department")
    const vatRate = parseFloat(state.order?.merchant?.vat)
    const consumptionTaxRate = parseFloat(state.order?.merchant?.consumption_tax)
    const deliveryFee = parseFloat(state.order?.merchant?.delivery_fee)

    useEffect(() => {
        if(invLogin && !isLoaded.current) {
            getOrder()
            isLoaded.current = true
            loadDepartments()
        }
    }, [invLogin, state.payload.page])

    useEffect(() => {
        if(state.filterPayload?.filter.length > 3) {
            loadProducts()
        }
    }, [state.filterPayload.filter])

    useEffect(() => {
        
        const orderAmount = state.selectedItems.reduce((total, item) => total + (item.sales_price * (item.quantity || 1)), 0);
        const vatAmount = (vatRate/100) * orderAmount
        const consumptionTax = (consumptionTaxRate/100) * orderAmount
        const orderTotal = orderAmount + vatAmount + consumptionTax + deliveryFee
        setState((prev) => ({
            ...prev,
            order: {
                ...prev.order,
                amount: orderAmount,
                vat: vatAmount,
                total: orderTotal
            },
        }));
    }, [state.selectedItems])

    const getOrder = () => {
        setState((prevState) => ({...prevState, loading: true}))
        dispatch(getOrderById({order_id: orderId, query: department}))
        .then((response) => {
            if(response?.payload?.status === true) {
                const result = response.payload?.data
                const orderItems = result?.items
                setState((prevState) => ({...prevState, order: result, selectedItems: orderItems}))
            }
        })
        .finally(() => setState((prevState) => ({...prevState, loading: false})))
    }

    const loadDepartments = () => {
        setState((prevState) => ({...prevState, loading: true}))
        dispatch(getAllDepartments())
        .then((response) => {
            if(response.payload?.status === true) {
                setState((prevState) => ({...prevState, departments: response?.payload?.data}))
            } 
            else {
                Swal.fire({
                    title: 'Error',
                    icon: 'error',
                    text: response.payload.message || 'Could not fetch departments',
                })
            }
        })
        .finally(() => setState((prevState) => ({...prevState, loading: false})))
    }

    const saveOrderChanges = () => {
        const assignments = state.order.items.map(item => ({
            item_id: item.id,
            department: state.orderAssignments[item.id] ?? item.department
        }));

        setState((prevState) => ({...prevState, loading: true}))
        dispatch(assignOrderToDepartment({id: state.order.id, assignments: assignments}))
        .then((response) => {
            if(response.payload?.status === true) {
                Swal.fire({
                    icon: 'success',
                    text: response.payload.message || 'Successful',
                })
            } 
            else {
                Swal.fire({
                    title: 'Error',
                    icon: 'error',
                    text: response.payload.message || 'Could not fetch departments',
                })
            }
        })
        .finally(() => setState((prevState) => ({...prevState, loading: false})))
    }

    const handleSelection = (orderItemId, department) => {
        setState((prevState) => ({...prevState, orderAssignments: {...prevState.orderAssignments, [orderItemId]: department}}))
    }

    const loadProducts = () => {
        setState((prevState) => ({...prevState, searching: true}))
        dispatch(getStoreProducts(state.filterPayload))
        .then((response) => {
            if(response.payload.status && response.payload.status === true) {
                setState((prevState) => ({
                    ...prevState, 
                    products: response.payload?.data?.data,
                    filteredItems: response.payload?.data?.data,
                }))
            }
        })
        .catch((error) => {
            console.error('Error fetching data: ', error);
        })
        .finally(() => setState((prevState) => ({...prevState, searching: false})))
    }

    const handleItemSelection = (item) => {
        const newItem = { ...state.selectedItem, id: null, quantity: state.quantity || 1 };
        setState((prev) => ({
            ...prev,
            selectedItems: prev.selectedItems.some((i) => i.product_id === newItem.id)
            ? prev.selectedItems.map((i) =>
                i.product_id === newItem.id
                    ? { ...i, reference: i.id, product_name: i.name, sales_price: i.sales_price, quantity: newItem.quantity }
                    : {...i, reference: i.id, product_name: i.name, sales_price: i.sales_price, quantity: 1}
                )
            : [...prev.selectedItems, newItem],
            selectedItem: null,
            quantity: 1,
        }));
    };

    const updateOrder = () => {

        const payload = {
            order_id: orderId,
            items: state.selectedItems
        }

        setState((prevState) => ({...prevState, sendingRequest: true}))

        dispatch(orderUpdate(payload))
        .then((response) => {
            if(response.payload?.status === true) {
                Swal.fire({
                    icon: 'success',
                    text: response.payload.message || 'Successful',
                })
            } 
            else {
                Swal.fire({
                    title: 'Error',
                    icon: 'error',
                    text: response.payload.message || 'Could not update order',
                })
            }
        })
        .finally(() => setState((prevState) => ({...prevState, sendingRequest: false})))
    }

    
    return (
        <Layout>
            <Container className="mt-4 px-4 mb-4">
                <InventoryConnectionButton />
                <Col md={12} className="row align-items-center justify-content-between flex-wrapd">
                    <div className="col-md-4">
                        <h3 className="m-0">Order Details</h3>
                        <p>
                            <small>Manage storefront order details</small>
                        </p>                        
                    </div>
                    <div className="col-md-8 text-right">
                        <Button 
                            type="button" 
                            className="border-0 mb-2 px-3 ms-2"
                            onClick={ () => navigate(user?.user_type === 'owner' ? '/inventory/storefront/orders' : '/members/inventory/storefront/orders')}
                        >
                            <FontAwesomeIcon icon={'receipt'}/>
                            <span className="ps-2">Orders</span>
                        </Button>
                        <Button 
                            type="button" 
                            className="border-0 mb-2 px-3 ms-2"
                            onClick={ () => navigate(user?.user_type === 'owner' ? '/inventory/storefront/products' : '/members/inventory/storefront/products')}
                        >
                            <FontAwesomeIcon icon={'box-open'}/>
                            <span className="ps-2">Products</span>
                        </Button>
                        <Button 
                            type="button" 
                            variant="primary" 
                            className="border-0 mb-2 ms-2"
                            onClick={ () => navigate( user?.user_type === 'owner' ? '/inventory/storefront' : '/members/inventory/storefront')}
                        >
                            <FontAwesomeIcon icon={'shop'} />
                            <span className="ps-2">Storefront</span>
                        </Button>
                        <Button 
                            type="button" 
                            variant="secondary" 
                            className="border-0 mb-2 ms-2 text-white"
                            onClick={ () => navigate(user?.user_type === 'owner' ? '/inventory/sell' : '/members/inventory/sell')}
                        >
                            <FontAwesomeIcon icon={'line-chart'} />
                            <span className="ps-2">Sell Product</span>
                        </Button>
                    </div>
                </Col>

                <div className="mt-4">
                    <Card className="border-0 shadow-sm rounded-14">
                        <Card.Header className="bg-white row">
                            <Col md={6} sm={12} xs={12}>
                                <h5>Order Details</h5> 
                                <p className="m-0">Order Amount: <strong>{formatCurrency(state.order.amount).with_currency}</strong> </p>
                                <p className="m-0">VAT: <strong>{formatCurrency(state.order.vat).with_currency}</strong> </p>
                                <p className="m-0">Consumption Tax: <strong>{formatCurrency(state.order.consumption_tax).with_currency}</strong> </p>
                                <p className="m-0">Processing Fee: <strong>{formatCurrency(state.order.fees).with_currency}</strong> </p>
                                <p className="m-0">Delivery Fee: <strong>{formatCurrency(state.order.delivery_fee).with_currency}</strong> </p>
                                <p className="m-0">Order Total: <strong>{formatCurrency(state.order.total).with_currency}</strong> </p>
                                <p className="m-0">Payment Status: {state.order.payment_status}</p>
                                <p className="m-0">Status: {state.order.order_status}</p>
                                <p className="m-0">Order Date: {dateFormatter(state.order.created_at)}</p>
                                <p className="m-0">Note/Address: {state.order.note}</p>
                            </Col>
                            <Col md={6} sm={12} xs={12}>
                                <h5>Customer Details</h5>
                                {
                                    state.order.customer ? (
                                        <>
                                            <p className="m-0">Full Name: {state.order.customer?.name}</p>
                                            <p className="m-0">Email: {state.order.customer?.email}</p>
                                            <p className="m-0">Phone Number: {state.order.customer?.phone_number}</p>
                                        </>
                                    ) : (
                                        <p className="m-0">Walk-in Customer</p>
                                    )
                                }
                            </Col>
                            <div className="text-right">
                                <Button 
                                    type="button" 
                                    variant="primary" 
                                    size="sm" 
                                    // disabled={state.order.payment_status === 'PAID'}
                                    onClick={() => setState((prev) => ({...prev, showForm: true}))}
                                >
                                    <FontAwesomeIcon icon={'plus'} /> Add items
                                </Button>
                            </div>
                        </Card.Header>
                        <Card.Body className="table-responsive-lg">
                            <Table size="sm" className="table-color table-striped">
                                    <thead>
                                        <tr>
                                            <th>Created By</th>
                                            <th>Item</th>
                                            <th>Price(₦)</th>
                                            <th>Quantity</th>
                                            <th>Amount(₦)</th>
                                            <th>Unit/Department</th>
                                        </tr>
                                    </thead>
                                    <tbody className="data-container">
                                        {
                                            state.loading ? (
                                                <tr className="in-page-loader">
                                                    <td colSpan={6}>
                                                        <Spinner size="lg" variant="primary"/>
                                                    </td>
                                                </tr>
                                            ) : null
                                        }
                                        {
                                            state.selectedItems?.length ? (
                                                state.selectedItems?.map((orderItem, index) => (
                                                    <tr key={orderItem.reference}>
                                                        <td>
                                                        {index+1}. {state.order?.user?.first_name+' '+state.order?.user?.last_name}
                                                        </td>
                                                        <td>{orderItem.product_name || orderItem?.name}</td>
                                                        <td>{formatCurrency(orderItem.sales_price).with_currency}</td>
                                                        <td>{formatCurrency(orderItem.quantity).formatted}</td>
                                                        <td>{formatCurrency(orderItem.quantity * orderItem.sales_price).with_currency}</td>
                                                        <td className="font-bold">
                                                            <select
                                                                className="form-control form-control-sm"
                                                                value={state.orderAssignments[orderItem.id] ?? orderItem.department}
                                                                onChange={(e) => handleSelection(orderItem.id, e.target.value)}
                                                            >
                                                                <option value="">----</option>
                                                                {state.departments?.map((department) => (
                                                                    <option key={department.id} value={department.name}>
                                                                        {department.name}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                        </td>
                                                    </tr>
                                                ))
                                            )
                                            : 
                                            (
                                                <tr>
                                                    <td colSpan={6} className="text-center text-muted">No data found</td>
                                                </tr>
                                            )
                                        }
                                    </tbody>
                            </Table>
                        </Card.Body>
                        <Card.Footer className="p-2 text-right bg-white">
                            <Button 
                                type="button" 
                                variant="primary" 
                                className="me-2"
                                onClick={updateOrder}
                                disabled={state.sendingRequest || state.selectedItems.length <= state.order?.items?.length}
                            >Save Order Changes</Button>
                            <Button 
                                type="button" 
                                variant="primary" 
                                onClick={saveOrderChanges}
                                disabled={state.loading || Object.keys(state.orderAssignments).length === 0}
                            >Save Order Assignment</Button>
                        </Card.Footer>
                    </Card>
                    <Modal
                        show={state.showForm}
                        onHide={() => setState((prev) => ({ ...prev, showForm: false, selectedItem: null }))}
                        centered
                        backdrop="static"
                    >
                        <Modal.Header closeButton className="bg-primary text-white py-1">
                            <Modal.Title as={"h5"}>Add Item</Modal.Title>
                        </Modal.Header>

                        <Modal.Body>
                            {!state.selectedItem ? (
                            <>
                                <Form.Control
                                    placeholder="Search items..."
                                    value={state.filterPayload.filter}
                                    onChange={(e) =>
                                        setState((prev) => ({
                                            ...prev,
                                            filterPayload: {
                                                ...prev.filterPayload,
                                                filter: e.target.value,
                                            },
                                        }))
                                    }
                                    className="mb-3"
                                />

                                <ListGroup>
                                    {
                                        state.searching ? (
                                            <div className="text-center py-2">
                                                <Spinner animation="border" size="sm" variant="primary" />
                                            </div>
                                        ) : null
                                    }
                                    {state.filteredItems && state.filteredItems.length === 0 && (
                                        <div className="text-muted text-center py-2">
                                          No items found
                                        </div>
                                    )}

                                    {state.filteredItems.map((item) => (
                                        <ListGroup.Item
                                            key={item.id}
                                            action
                                            onClick={() =>
                                                setState((prev) => ({ ...prev, selectedItem: item }))
                                            }
                                            className="d-flex justify-content-between"
                                        >
                                            <span>{item.name}</span>
                                            <strong>{formatCurrency(item.sales_price).with_currency}</strong>
                                        </ListGroup.Item>
                                    ))}
                                </ListGroup>
                            </>
                            ) : (
                            <>
                                <div className="mb-3">
                                <h6>{state.selectedItem.name}</h6>
                                <strong>{formatCurrency(state.selectedItem.sales_price).with_currency}</strong>
                                </div>
                                <Form.Group>
                                    <Form.Label>Quantity</Form.Label>
                                    <Form.Control
                                        type="number"
                                        min={1}
                                        value={state.quantity || 1}
                                        onChange={(e) =>
                                            setState((prev) => ({
                                                ...prev,
                                                quantity: parseInt(e.target.value) || 1,
                                            }))
                                        }
                                    />
                                </Form.Group>
                            </>
                            )}
                        </Modal.Body>

                        <Modal.Footer className="py-1">
                            {state.selectedItem ? (
                            <Button
                                variant="primary"
                                onClick={handleItemSelection}
                            >
                                Add to Order
                            </Button>
                            ) : (
                            <Button
                                variant="primary"
                                onClick={() =>
                                 setState((prev) => ({ ...prev, showForm: false, selectedItem: null }))
                                }
                            >
                                Cancel
                            </Button>
                            )}
                        </Modal.Footer>
                    </Modal>
                </div>
            </Container>
        </Layout>
    );
}

export default OrderDetails;